import type { PublicContestStandings } from '@/features/competition/types'

export type ProgramItemKind =
  | 'slot'
  | 'activity'
  | 'competition_round'
  | 'cake_moment'
  | 'award_reveal'
  | 'custom'

export type ProgramItemStatus = 'scheduled' | 'running' | 'done' | 'skipped'

export interface PublicProgramItem {
  id: string
  kind: ProgramItemKind
  title: string
  description: string
  starts_at: string | null
  ends_at: string | null
  location: string | null
  host: string | null
  category: string | null
  media_url: string | null
  status: ProgramItemStatus
  actual_started_at: string | null
}

export interface PublicCakeMoment {
  name: string
  description: string
  message: string
  media_url: string | null
  scheduled_at: string | null
  program_item_id: string | null
}

export interface ScreenState {
  event_name: string
  locale: string
  now: PublicProgramItem | null
  next: PublicProgramItem | null
  later: PublicProgramItem[]
  countdown_seconds: number | null
  refresh_seconds: number
  server_time: string
  /** 🎂 Présent uniquement pendant que l'étape du gâteau tourne. */
  cake: PublicCakeMoment | null
  /** Scrutins ouverts à la salle, par leur nom — jamais un score. */
  open_votes: string[]
  /** Un départage à cinq jurés est en cours. */
  tiebreak_running: boolean
  /** Titres acquis : publiés, et départagés s'il le fallait. */
  winners: ScreenWinner[]
  /** Décomptes des votes ouverts : à zéro, « temps écoulé » — le vote reste ouvert. */
  vote_timers?: { name: string; ends_at: string }[]
  /** Qui défile maintenant, désigné par le MC pendant un tour en cours. */
  on_stage?: ScreenOnStage | null
  /** Un tour du concours tourne : la télé relit plus souvent. */
  round_running?: boolean
  /** Le détail des votes ouverts : nommés en photo, décompte. Aucun bouton. */
  open_vote_boards?: ScreenOpenVote[]
  /** Les classements publiés — vides avant, et de nouveau si l'on dépublie. */
  results?: PublicContestStandings[]
  /** Posé par le MC par-dessus tout l'écran ; absent des serveurs plus anciens. */
  overlay?: ScreenOverlay | null
}

/**
 * Un titre proclamé. Photo et numéro pour l'annonce en grand ; absents des
 * serveurs plus anciens, d'où les champs facultatifs. Jamais un score.
 */
export interface ScreenWinner {
  contest: string
  name: string
  competition_category_id?: string | null
  candidate_id?: string | null
  number?: number | null
  photo_url?: string | null
  /** Les scores de ce concours sont publiés : son tableau est à l'écran. */
  published?: boolean
}

/** Le profil public d'un candidat, tel que la télé l'affiche. Rien de privé. */
export interface ScreenCandidate {
  id: string
  number: number | null
  name: string
  photo_url: string | null
  contest: string
  age: number | null
  activity: string | null
  education_level: string | null
  motto: string | null
  three_words: string[]
}

export interface ScreenOnStage {
  round: { id: string; name: string }
  candidate: ScreenCandidate
}

export interface ScreenOpenVote {
  vote_category_id: string
  title: string
  contest: string | null
  round: string | null
  nominees: ScreenCandidate[]
  /** Zéro du décompte annoncé ; ne ferme rien. */
  countdown_ends_at: string | null
}

/**
 * Un candidat sur la grille du pronostic : son profil public entier. Les
 * champs du profil manquent sur un serveur plus ancien — facultatifs.
 */
export interface OverlayCandidate {
  id: string
  number: number | null
  name: string
  photo_url: string | null
  age?: number | null
  activity?: string | null
  education_level?: string | null
  motto?: string | null
  three_words?: string[]
  /** La présentation, dans la langue de l'écran ; "" sans texte. */
  bio?: string
}

/** Ce que le MC pose par-dessus l'écran : la grille des candidats, ou un gagnant. */
export type ScreenOverlay =
  | {
      kind: 'candidates'
      title: string
      categories: {
        id: string
        name: string
        candidates: OverlayCandidate[]
      }[]
      published_at: string
    }
  | {
      kind: 'announcement'
      title: string
      winner: string
      detail: string | null
      published_at: string
    }
  | {
      /** Une question du quiz : jamais sa bonne réponse avant la révélation. */
      kind: 'quiz'
      title: string
      number: number
      total: number
      question: string
      answers: { letter: QuizLetter; text: string }[]
      revealed_letter: QuizLetter | null
      published_at: string
    }

// ── La feuille du MC : sous-parties d'une étape ────────────────────

export type SegmentKind = 'note' | 'prognostic' | 'announcement' | 'performances' | 'quiz'
export type PerformanceChoice = 'sing' | 'dance' | 'talk'

/** Un candidat et ce qu'il fera au moment « chanter, danser ou parler ». */
export interface PerformanceRow {
  candidate_id: string
  number: number | null
  name: string
  photo_url: string | null
  choice: PerformanceChoice | null
  topic: string | null
  notes: string | null
}

/** Une sous-partie d'une étape — privée au MC, jamais au programme public. */
export interface ProgramSegment {
  id: string
  program_item_id: string
  position: number
  kind: SegmentKind
  title: string
  body: string | null
  competition_category_id: string | null
  winner: string | null
  winner_detail: string | null
  performances: PerformanceRow[]
  /** « quiz » : les questions, dans l'ordre où le MC les pose. */
  questions?: QuizQuestion[]
  /** Ce que cette sous-partie affiche à l'écran en ce moment. */
  on_screen: 'candidates' | 'announcement' | 'quiz' | null
}

export type QuizLetter = 'A' | 'B' | 'C' | 'D'
export const QUIZ_LETTERS: QuizLetter[] = ['A', 'B', 'C', 'D']

/** Une question du quiz, telle que le MC la prépare — bonne réponse comprise. */
export interface QuizQuestion {
  id: string
  segment_id: string
  position: number
  question: string
  /** Toujours quatre : A, B, C, D. */
  answers: string[]
  /** 0 = A … 3 = D ; `null` : pas de bonne réponse (quiz d'opinion). */
  correct_index: number | null
  note: string | null
  on_screen: boolean
  revealed: boolean
}

export interface QuizQuestionFields {
  question: string
  answers: string[]
  correct_index: number | null
  note: string | null
}

export interface SegmentFields {
  title: string
  body: string | null
  competition_category_id: string | null
  winner: string | null
  winner_detail: string | null
}

/** Conducteur de l'équipe — étapes internes comprises. */
export interface ConductorItem {
  id: string
  kind: ProgramItemKind
  title_i18n: Record<string, string>
  description_i18n: Record<string, string>
  starts_at: string | null
  ends_at: string | null
  location: string | null
  host: string | null
  is_public: boolean
  position: number
  status: ProgramItemStatus
  actual_started_at: string | null
  actual_ended_at: string | null
  /** L'animation, le tour ou la remise que l'étape désigne, selon `kind`. */
  ref_id: string | null
  /** Notes du MC — jamais montrées au public ni à l'écran de la salle. */
  notes: string | null
}

// ── Préparation du déroulé ─────────────────────────────────────────

/** Ce que l'éditeur doit savoir de l'édition : langues, fuseau, nuit. */
export interface PlanEvent {
  id: string
  starts_at: string
  timezone: string
  default_locale: string
  locales: string[]
}

export interface PlanActivity {
  id: string
  name_i18n: Record<string, string>
  duration_minutes: number | null
  is_active: boolean
}

export interface PlanStepFields {
  title_i18n: Record<string, string>
  starts_at: string | null
  duration_minutes: number | null
  activity_id: string | null
  host: string | null
  location: string | null
  notes: string | null
  is_public: boolean
}

export interface FlowResult {
  item: ConductorItem
  closed: ConductorItem | null
}

// ── Concours, piloté depuis le conducteur ──────────────────────────

export type RoundStatus = 'pending' | 'running' | 'done'
export type VoteStatus = 'closed' | 'open' | 'frozen' | 'published'

/** Qui est sur scène : numéro et nom public, ce que le MC lit au micro. */
export interface RoundParticipant {
  candidate_id: string
  candidate_number: number | null
  name: string
  result: string
  notes: string | null
}

export interface ConductorRound {
  id: string
  competition_category_id: string | null
  name_i18n: Record<string, string>
  status: RoundStatus
  started_at: string | null
  ended_at: string | null
  program_item_id: string | null
  position: number
  participants: RoundParticipant[]
  /** Qui défile à cet instant : l'écran de la salle montre son profil. */
  on_stage_candidate_id?: string | null
}

/** Le scrutin d'un tour, tel que le MC le conduit. Jamais de score ici. */
export interface ConductorVote {
  id: string
  name_i18n: Record<string, string>
  competition_category_id: string | null
  competition_round_id: string | null
  is_tiebreak: boolean
  status: VoteStatus
  ballots: number
  candidate_ids: string[]
  /** Durée choisie pour le décompte annoncé à la salle — gardée pour la reproposer. */
  countdown_seconds: number | null
  /** Zéro du décompte, présent seulement pendant un vote ouvert. Ne ferme rien. */
  countdown_ends_at: string | null
  /** Heure du serveur à la lecture : corrige l'horloge du téléphone. */
  server_now: string | null
}

export interface ConductorContest {
  id: string
  code: string
  name_i18n: Record<string, string>
}
